import styles from './Form.module.scss';

import {
  ChangeEvent,
  FormEvent,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { ContextValue, FormContext, FormField } from './FormContext';
import { FormInput } from './FormInputs';

export type OnSubmit = (
  event: FormEvent<HTMLFormElement>,
  extra: {
    inputs: Map<string, FormInput[]>;
    fields: Map<string, FormField>;
  },
) => void;

interface Props {
  children: ReactNode;
  onChange?: (event: ChangeEvent<HTMLFormElement>) => void;
  onSubmit?: OnSubmit;
}

function Implementation(
  { ...props }: Props,
  ref: ForwardedRef<HTMLFormElement>,
) {
  const inputs = useRef(new Map<string, FormInput[]>());
  const fields = useRef(new Map<string, FormField>());

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    props.onSubmit?.(event, { inputs: inputs.current, fields: fields.current });
  };

  const contextValue = useMemo<ContextValue>(() => {
    return {
      inputs: inputs.current,
      fields: fields.current,
    };
  }, []);

  return (
    <FormContext.Provider value={contextValue}>
      <form
        ref={ref}
        className={styles['form']}
        onChange={props.onChange}
        onSubmit={onSubmit}
      >
        {props.children}
      </form>
    </FormContext.Provider>
  );
}

export const Form = forwardRef<HTMLFormElement, Props>(Implementation);
